<template>
  <div class="password-requirements" :class="{ 'all-requirements-met': allRequirementsMet }">
    <h4 class="requirements-title" :class="{ 'title-fade': allRequirementsMet }">Password Requirements</h4>
    <ul class="requirements-list">
      <li 
        v-for="requirement in displayRequirements" 
        :key="requirement.name"
        class="requirement-item"
          :class="{ 
          'requirement-met': requirement.met,
          'requirement-warning': requirement.warning && !requirement.met,
          'requirement-fade': shouldFadeRequirement(requirement)
        }"
      >
        <span class="requirement-icon">
          <i :class="getRequirementIcon(requirement)"></i>
        </span>
        <span class="requirement-text">{{ requirement.text }}</span>
      </li>
      
      <!-- Password Match Requirement -->
      <li 
        v-if="!passwordsMatch || !allRequirementsMet"
        class="requirement-item"
        :class="{ 
          'requirement-met': passwordsMatch,
          'requirement-fade': passwordsMatch
        }"
      >
        <span class="requirement-icon">
          <i :class="passwordsMatch ? 'fas fa-check' : 'fas fa-times'"></i>
        </span>
        <span class="requirement-text">Passwords must match</span>
      </li>
      
      <!-- Strength Requirement - Only show if weak passwords are NOT allowed -->
      <li 
        v-if="!isWeakPasswordsAllowed && (strengthLevel < 3 || !allRequirementsMet)"
        class="requirement-item"
        :class="{ 
          'requirement-met': strengthLevel >= 3,
          'requirement-fade': strengthLevel >= 3
        }"
      >
        <span class="requirement-icon">
          <i :class="strengthLevel >= 3 ? 'fas fa-check' : 'fas fa-times'"></i>
        </span>
        <span class="requirement-text">Password strength must be at least "Strong"</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'PasswordRequirements',
  props: {
    password: {
      type: String,
      default: ''
    },
    confirmPassword: {
      type: String,
      default: ''
    },
    policy: {
      type: Object,
      default: null
    },
    errors: {
      type: Array,
      default: () => []
    },
    strengthLevel: {
      type: Number,
      default: 0
    },
    requirements: {
      type: Array,
      default: () => []
    },
    allowWeakPasswords: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // Check if weak passwords are allowed from either prop or policy
    isWeakPasswordsAllowed() {
      return this.allowWeakPasswords || (this.policy && this.policy.allowSubmissionOfWeakPasswords === true);
    },
    displayRequirements() {
      // If we have requirements passed from parent, use those
      if (this.requirements && this.requirements.length > 0) {
        return this.requirements;
      }
      
      // Default requirements when policy is not loaded yet
      const defaultRequirements = [
        {
          name: 'length',
          met: this.password.length >= 16,
          text: 'At least 16 characters'
        },
        {
          name: 'uppercase',
          met: /[A-Z]/.test(this.password),
          text: 'At least one uppercase letter (A-Z)'
        },
        {
          name: 'lowercase',
          met: /[a-z]/.test(this.password),
          text: 'At least one lowercase letter (a-z)'
        },
        {
          name: 'digit',
          met: /\d/.test(this.password),
          text: 'At least one number (0-9)'
        },
        {
          name: 'special',
          met: /[!@#$%^&*()_+=\[\]{};:<>|./?,-]/.test(this.password),
          text: 'At least one special character (!@#$%^&*)'
        }
      ];
      
      if (!this.policy) return defaultRequirements;
      
      return [
        {
          name: 'length',
          met: this.password.length >= this.policy.minimumLength,
          text: `At least ${this.policy.minimumLength} characters`
        },
        {
          name: 'uppercase',
          met: !this.policy.requireUppercase || /[A-Z]/.test(this.password),
          text: 'At least one uppercase letter (A-Z)',
          show: this.policy.requireUppercase
        },
        {
          name: 'lowercase',
          met: !this.policy.requireLowercase || /[a-z]/.test(this.password),
          text: 'At least one lowercase letter (a-z)',
          show: this.policy.requireLowercase
        },
        {
          name: 'digit',
          met: !this.policy.requireDigit || /\d/.test(this.password),
          text: 'At least one number (0-9)',
          show: this.policy.requireDigit
        },
        {
          name: 'special',
          met: !this.policy.requireSpecialCharacter || /[!@#$%^&*()_+=\[\]{};:<>|./?,-]/.test(this.password),
          text: 'At least one special character (!@#$%^&*)',
          show: this.policy.requireSpecialCharacter
        }
      ].filter(req => req.show !== false);
    },
    
    passwordsMatch() {
      return this.password === this.confirmPassword && this.password.length > 0;
    },
    
    allRequirementsMet() {
      const requiredRequirements = this.displayRequirements.filter(req => req.required !== false);
      const allRequiredMet = requiredRequirements.every(req => req.met);
      const matchesMet = this.passwordsMatch;
      
      // Only consider strength requirement if weak passwords are not allowed
      const strengthMet = this.isWeakPasswordsAllowed || this.strengthLevel >= 3;
      
      return allRequiredMet && matchesMet && strengthMet;
    },
    
    visibleRequirements() {
      // Only show unmet requirements or warnings
      return this.displayRequirements.filter(req => !req.met || req.warning);
    }
  },
  
  methods: {
    getRequirementIcon(requirement) {
      if (requirement.warning && !requirement.met) {
        return 'fas fa-exclamation-triangle'; // Warning icon for blocked passwords
      }
      return requirement.met ? 'fas fa-check' : 'fas fa-times';
    },
    
    shouldFadeRequirement(requirement) {
      // For blocked password requirement with special behavior
      if (requirement.name === 'blocked' && requirement.showOnlyWhenRelevant) {
        // Don't fade if there's a warning (blocked word found) - keep it visible
        // Fade when there's no warning (no blocked word) OR when the requirement is met
        return !requirement.warning || requirement.met;
      }
      
      // For regular required requirements - fade when met
      if (requirement.required !== false) {
        return requirement.met;
      }
      
      // Default - don't fade
      return false;
    }
  }
};
</script>

<style scoped>
.password-requirements {
  margin-bottom: 20px;
  padding: 12px;
  background-color: transparent;
  border-radius: 6px;
  border: 1px solid #E3E8EA;
  transition: all 0.4s ease;
  overflow: hidden;
  max-height: 500px; /* Reasonable max height */
  margin-bottom: 20px;
}

.password-requirements.all-requirements-met {
  max-height: 0;
  padding: 0;
  margin: 0;
  border: none;
  opacity: 0;
}

.requirements-title {
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: bold;
  color: #fff;
  transition: all 0.4s ease;
  margin-left: 6px;
  text-align: left;
}

.requirements-title.title-fade {
  opacity: 0;
  max-height: 0;
  margin: 0;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  transition: all 0.4s ease;
  padding-right: 5px
}

.requirement-item {
  display: flex;
  align-items: center;
  padding: 1px 0;
  color: #fff;
  transition: all 0.4s ease;
  font-size: 11px;
  opacity: 1;
  max-height: 20px;
  margin-bottom: 2px;
}

.requirement-item.requirement-met {
  color: #007F3B;
}

.requirement-item.requirement-warning {
  color: #FFA500; /* Orange for warnings */
}

.requirement-item.requirement-fade {
  opacity: 0;
  max-height: 0;
  padding: 0;
  margin-bottom: 0;
  transform: translateY(-10px);
}

.requirement-icon {
  width: 20px;
  margin-right: 8px;
  display: flex;
  justify-content: center;
}

.requirement-text {
  flex: 1;
}

/* Error Styling */
.password-errors {
  margin-top: 16px;
  padding: 12px;
  background-color: #F8D7DA;
  border: 1px solid #F5C6CB;
  border-radius: 4px;
}

.error-title {
  font-weight: 600;
  color: #721C24;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.error-list {
  list-style: none;
  padding: 0;
  margin: 0;
  color: #721C24;
}

.error-list li {
  padding: 2px 0;
  font-size: 14px;
}
</style>